import { useMemo } from 'react';
import { Box, Chip, Divider, MenuItem, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { NumericTextField } from '../../../../components/forms/NumericTextField';
import type { RockUCSNSSpecimen } from '../../../../types/rockUcsNs';
import type { UcsNsSpecimenPreview } from '../computations';
import { buildFailureDescriptionPreview } from '../../rock-ucs/computations';
import {
  UCSNS_FAILURE_MODE_OPTIONS,
  UCSNS_PLANE_TYPE_OPTIONS,
} from '../rockUcsNsFields';

/**
 * Failure modes that use a secondary plane in the auto-description template.
 * Mirrors FAILURE_MODES_WITH_SECONDARY in rock-ucs/rockUcsFields.ts.
 */
const FAILURE_MODES_WITH_SECONDARY = new Set<string>([
  'Multiple Shear',
  'Shear & Axial Cleavage',
  'Multiple Shear & Axial Cleavage',
]);
import { DerivedValue, fmt, inputSx, paperSx, SectionHeader, unit } from '../../rock-tcs/sectionStyles';
import { useTestFormulas } from '../../../../hooks/useTestFormulas';
// Reuse the animated failure-mode reference diagrams from rock-ucs
// (same 4 base modes: Shear / Multiple Shear / Axial Cleavage / Fragmented)
import { FailureModeReference } from '../../rock-ucs/sections/FailureModeReference';

interface Props {
  specimen: RockUCSNSSpecimen;
  preview: UcsNsSpecimenPreview;
  title: string;
  onChange: (field: keyof RockUCSNSSpecimen, value: RockUCSNSSpecimen[keyof RockUCSNSSpecimen]) => void;
  readOnly?: boolean;
}

/**
 * Result Entry — one specimen's card (ASTM D7012 Method C / Test B):
 *   - Single "Maximum Load at Failure (kN)" → derived UCS (MPa)
 *   - Test Duration (s) manual input
 *   - Derived Stress Rate (MPa/s) if rate-of-loading is entered
 *   - Full TCS-style failure-mode block (mode toggle, plane type, angle,
 *     auto failure description with override, failure remarks)
 *
 * Modelled on rock-bts/sections/SpecimenResultCard.tsx + rock-ucs/sections/FailureModeSection.tsx.
 * Plan item: 5.4.
 */
export const SpecimenResultCard = ({ specimen, preview, title, onChange, readOnly }: Props) => {
  const num = (field: keyof RockUCSNSSpecimen) => (value: number | null) => onChange(field, value);
  // Formula-registry tooltips for the computed tiles (FEAT "Formula Registry").
  const { formulaFor } = useTestFormulas('rockUcsNs');

  const hasSecondary = specimen.failureMode
    ? FAILURE_MODES_WITH_SECONDARY.has(specimen.failureMode)
    : false;

  const autoDescription = useMemo(
    () =>
      buildFailureDescriptionPreview({
        mode: specimen.failureMode,
        primaryPlaneType: specimen.primaryPlaneType,
        primaryPlaneAngleDeg: specimen.primaryPlaneAngleDeg,
        secondaryPlaneType: specimen.secondaryPlaneType,
        secondaryPlaneAngleDeg: specimen.secondaryPlaneAngleDeg,
      }),
    [
      specimen.failureMode,
      specimen.primaryPlaneType,
      specimen.primaryPlaneAngleDeg,
      specimen.secondaryPlaneType,
      specimen.secondaryPlaneAngleDeg,
    ],
  );

  return (
    <Paper variant="outlined" sx={paperSx}>
      <SectionHeader label={title} />

      {/* ── Loading & Result ─────────────────────────────────────────────── */}
      <SectionHeader label="Loading Information" color="text.secondary" />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 1 }}>
        <NumericTextField
          size="small"
          label="Maximum Load at Failure (kN) *"
          value={specimen.maxLoadKN}
          onChange={num('maxLoadKN')}
          InputProps={{ endAdornment: unit('kN'), readOnly }}
          sx={inputSx}
        />
        <DerivedValue
          label="Uniaxial Compressive Strength"
          value={fmt(preview.computedUCSMPa, 1)}
          suffix="MPa"
          formula={formulaFor('ComputedUCSMPa')?.formula}
          note={formulaFor('ComputedUCSMPa')?.note}
        />
      </Box>

      {/* Stress Rate row — visible if rate-of-loading was entered on Parameters tab */}
      {(specimen.rateOfLoadingKNs !== null || preview.stressRateMPaS !== null) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 1 }}>
          <DerivedValue
            label="Stress Rate (derived)"
            value={fmt(preview.stressRateMPaS, 3)}
            suffix="MPa/s"
            formula={formulaFor('ComputedStressRateMPaS')?.formula}
            note={formulaFor('ComputedStressRateMPaS')?.note}
          />
          {preview.rateOutsideAstmRange === true && (
            <Chip
              size="small"
              label="Rate outside ASTM D7012 band (advisory)"
              color="warning"
              sx={{ height: 18, fontSize: '0.65rem', alignSelf: 'center' }}
            />
          )}
        </Box>
      )}

      {/* Test Duration — total seconds AND decimal minutes (BUG-2026-0138 item 8,
          same display rule as the TCS/UCS Peak Test Results showMinutes rows) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 1 }}>
        <NumericTextField
          size="small"
          label="Test Duration (s)"
          value={specimen.durationS}
          onChange={num('durationS')}
          InputProps={{ endAdornment: unit('s'), readOnly }}
          sx={inputSx}
        />
        {specimen.durationS !== null && (
          <DerivedValue
            label="Duration"
            value={`${fmt(specimen.durationS, 0)} s (${fmt(specimen.durationS / 60, 2)} min)`}
          />
        )}
      </Box>

      {/* ── Failure Mode ─────────────────────────────────────────────────── */}
      <Divider sx={{ my: 1 }} />
      <SectionHeader label="Failure Mode" color="text.secondary" />

      <ToggleButtonGroup
        exclusive
        size="small"
        value={specimen.failureMode}
        onChange={(_, v) => {
          if (!readOnly) onChange('failureMode', v);
        }}
        sx={{
          flexWrap: 'wrap',
          mb: 1.5,
          '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.72rem', py: 0.4 },
        }}
      >
        {UCSNS_FAILURE_MODE_OPTIONS.map((m) => (
          <ToggleButton key={m} value={m}>
            {m}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {/* Plane type + angle rows */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.25, mb: 1 }}>
        <TextField
          select
          size="small"
          label="Primary Plane"
          value={specimen.primaryPlaneType ?? ''}
          onChange={(e) => onChange('primaryPlaneType', e.target.value || null)}
          InputProps={{ readOnly }}
          sx={inputSx}
        >
          <MenuItem value="">—</MenuItem>
          {UCSNS_PLANE_TYPE_OPTIONS.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>
        <NumericTextField
          size="small"
          label="Angle to Load Axis (°)"
          value={specimen.primaryPlaneAngleDeg}
          onChange={num('primaryPlaneAngleDeg')}
          InputProps={{ endAdornment: unit('°'), readOnly }}
          sx={inputSx}
        />
        <TextField
          select
          size="small"
          label="Secondary Plane (optional)"
          value={specimen.secondaryPlaneType ?? ''}
          onChange={(e) => onChange('secondaryPlaneType', e.target.value || null)}
          InputProps={{ readOnly }}
          sx={inputSx}
          disabled={!hasSecondary}
        >
          <MenuItem value="">—</MenuItem>
          {UCSNS_PLANE_TYPE_OPTIONS.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>
        <NumericTextField
          size="small"
          label="Secondary Angle (°)"
          value={specimen.secondaryPlaneAngleDeg}
          onChange={num('secondaryPlaneAngleDeg')}
          InputProps={{ endAdornment: unit('°'), readOnly }}
          sx={inputSx}
          disabled={!hasSecondary}
        />
      </Box>

      {/* Failure mode reference diagrams */}
      <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
        <FailureModeReference selectedMode={specimen.failureMode} />
      </Box>

      {/* ── Auto Description + Override + Remarks ─────────────────────── */}
      <Divider sx={{ my: 1 }} />
      <SectionHeader label="Failure Description" color="text.secondary" />
      <Typography
        variant="caption"
        sx={{ fontSize: '0.72rem', display: 'block', mb: 0.75, fontStyle: 'italic' }}
      >
        Auto-generated:{' '}
        {autoDescription ?? '— select a failure mode to generate the description —'}
      </Typography>
      <Box sx={{ display: 'grid', rowGap: 1.25 }}>
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label="Override (leave empty to use the auto-generated sentence)"
          value={specimen.failureDescriptionOverride ?? ''}
          onChange={(e) => onChange('failureDescriptionOverride', e.target.value || null)}
          InputProps={{ readOnly }}
          sx={inputSx}
        />
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label="Failure Remarks (internal — not printed on the report)"
          value={specimen.failureRemarks ?? ''}
          onChange={(e) => onChange('failureRemarks', e.target.value || null)}
          InputProps={{ readOnly }}
          sx={inputSx}
        />
      </Box>
    </Paper>
  );
};
